/**
 * ──────────────────────────────────────────────────────────────────────────
 *  CONTEXT-LOCKED PALETTE — Camera Smoke family (E.W.5,
 *  operator-cop-doctrine §3A.7 context-locking / §3B.8 the AMBIENT register /
 *  §9.1 camera-CV smoke is a pre-fusion, lower-confidence signal).
 *
 *  This module is the SINGLE source of the camera-CV smoke palette. It is consumed
 *  ONLY by the camera-smoke family:
 *    - CameraSmokeLayer map markers (the ambient amber camera-glyph dot)
 *    - the CameraSmokeBody side-panel badge + legend
 *
 *  Camera smoke detections are AMBIENT pre-fusion context — deliberately a quiet
 *  SMOKY AMBER, distinct from the vivid Response-Level Anomaly-Queue dots
 *  (styles/responseLevel) that the F.1 fusion engine produces, and from the white
 *  live-camera glyph. SHAPE encodes "a camera saw smoke" (a small amber dot
 *  carrying a camera glyph — the camera glyph is the family's visual signature,
 *  distinct from the round source-coloured raw-hotspot dot and the pulsing anomaly
 *  dot); MOTION is NOT in this family's budget (it never pulses). COLOR is never
 *  load-bearing alone — every render pairs the hue with the camera name + a
 *  "camera smoke" label (panel + glyph).
 *
 *  Overlap debt this lock acknowledges (§3A.7): this smoky amber is a cousin of the
 *  IMS Response Level 3 / OC-Monitored / outage ambers that mean other things in
 *  OTHER families. Those families own their own vivid hexes; this module owns its
 *  OWN amber and must never import a sibling palette. SHAPE (a dot carrying a
 *  CAMERA glyph, not a flame / outage-area polygon / pulsing anomaly dot / round
 *  raw-hotspot dot) keeps the families distinguishable in monochrome.
 *
 *  ⛔ Do NOT import from ./responseLevel / ./fireStages / ./capHazards /
 *     ./communityStatus / ./rawHotspots / ./lifecycleStates / ./sectorStyles /
 *     ./outageStyles / ./aircraftRoles / ./floodGauges / ./roadClosureStyles
 *     (enforced by the no-restricted-imports lint rule in eslint.config.js).
 * ──────────────────────────────────────────────────────────────────────────
 */

/** The single source-of-truth amber for the camera-smoke family. Colour Law 4
 *  desaturate (#e8a13a → #c69a5e) so this ambient pre-fusion signal sits in the
 *  S ≤ 45% ambient band, ≥30 saturation-points below the act register. */
export const CAMERA_SMOKE_COLOR = '#c69a5e'; // desaturated smoky amber (Law 4)

/** Ambient dot diameter (px) — small, the anchor + click target beneath the glyph. */
export const CAMERA_SMOKE_DOT_SIZE = 8;
/** Camera glyph diameter (px) — the family's visual signature, on top of the dot. */
export const CAMERA_SMOKE_GLYPH_SIZE = 15;
/** Fill opacity — solid enough to read, quiet ambient register. */
export const CAMERA_SMOKE_DOT_ALPHA = 0.9;

/** A coarse confidence band, paired with the numeric value (never colour-alone). */
export function cameraSmokeConfidenceBand(confidence: number | null): string {
  if (confidence == null) return 'unknown';
  if (confidence < 50) return 'low';
  if (confidence < 80) return 'nominal';
  return 'high';
}
